/** Formatage — tout en français, unités du contrat (euros HT, ISO 8601). */

const EUROS = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const JOUR = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const JOUR_HEURE = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** Montants en euros HT. Un zéro reste « 0 € » : c'est une information. */
export function euros(montant: number | null | undefined): string {
  if (montant === null || montant === undefined || Number.isNaN(montant)) return '—';
  // Au-delà du millier, l'unité exacte n'apporte rien dans une barre latérale.
  if (Math.abs(montant) >= 1000) return `${EUROS.format(Math.round(montant / 1000) * 1000)}`;
  return EUROS.format(montant);
}

export function pourcentage(part: number | null | undefined): string {
  if (part === null || part === undefined || Number.isNaN(part)) return '—';
  return `${Math.round(part * 100)} %`;
}

/** Minuit local du jour d'une date, pour raisonner en jours de calendrier. */
function minuit(valeur: Date): number {
  return new Date(valeur.getFullYear(), valeur.getMonth(), valeur.getDate()).getTime();
}

function versDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function date(iso: string | null | undefined): string {
  const valeur = versDate(iso);
  return valeur ? JOUR.format(valeur) : '—';
}

export function dateHeure(iso: string | null | undefined): string {
  const valeur = versDate(iso);
  return valeur ? JOUR_HEURE.format(valeur) : '—';
}

/**
 * Ancienneté en clair : « aujourd'hui », « il y a 3 jours », « il y a 2 mois ».
 * C'est cette formulation, et non la date brute, qui fait comprendre au
 * commercial qu'il lit un bilan de trois mois.
 */
export function anciennete(iso: string | null | undefined, maintenant = Date.now()): string {
  const valeur = versDate(iso);
  if (!valeur) return '';
  // Écart en jours de calendrier, pas en tranches de 24 h : une synthèse
  // produite avant-hier soir doit se lire « il y a 2 jours », pas « hier ».
  const jours = Math.round((minuit(new Date(maintenant)) - minuit(valeur)) / 86_400_000);
  if (jours < 0) return 'à venir';
  if (jours === 0) return "aujourd'hui";
  if (jours === 1) return 'hier';
  if (jours < 31) return `il y a ${jours} jours`;
  const mois = Math.round(jours / 30.44);
  if (mois < 24) return `il y a ${mois} mois`;
  return `il y a ${Math.round(jours / 365.25)} ans`;
}

/** Délai restant avant une échéance : « dans 2 h 15 », « échéance passée ». */
export function delaiRestant(iso: string | null | undefined, maintenant = Date.now()): string | null {
  const valeur = versDate(iso);
  if (!valeur) return null;
  const minutes = Math.round((valeur.getTime() - maintenant) / 60_000);
  if (minutes <= 0) return 'échéance passée';
  if (minutes < 60) return `dans ${minutes} min`;
  const heures = Math.floor(minutes / 60);
  const reste = minutes % 60;
  if (heures < 24) return reste ? `dans ${heures} h ${String(reste).padStart(2, '0')}` : `dans ${heures} h`;
  return `dans ${Math.round(heures / 24)} j`;
}

/**
 * Libellés du contrat (§3.2, table de l'énumération `canal`). Ils y sont donnés
 * explicitement « pour que le plugin ne les invente pas » : on les reprend mot
 * pour mot, sauf `inconnu` — seul dans une ligne de méta, « Inconnu » ne dit pas
 * de quoi il s'agit.
 *
 * `via_integrateur` est le cas le plus fréquent : le corpus ne permet pas
 * toujours de trancher entre « confié » et « apporté ».
 */
const CANAUX: Record<string, string> = {
  direct: 'Direct',
  via_fabricant: 'Via fabricant',
  via_integrateur: 'Via intégrateur',
  confie_integrateur: 'Confié à un intégrateur',
  apporte_integrateur: "Apporté par l'intégrateur",
  inconnu: 'Canal inconnu',
};

/**
 * Dernier recours pour une valeur que le contrat ne prévoit pas encore.
 *
 * Le pipeline peut servir un code inconnu du plugin — `via_fabricant` est
 * apparu ainsi, alors que l'énumération du contrat ne le liste pas. Afficher
 * le jeton machine tel quel à un commercial est laid et incompréhensible ;
 * on le rend lisible sans lui inventer de sens. La correction de fond reste
 * de faire évoluer le contrat côté pipeline.
 */
function lisible(valeur: string): string {
  const mots = valeur.replace(/_/g, ' ').trim();
  return mots.charAt(0).toUpperCase() + mots.slice(1);
}

export function canal(valeur: string | null | undefined): string {
  if (!valeur) return CANAUX.inconnu as string;
  return CANAUX[valeur] ?? lisible(valeur);
}

const METHODES: Record<string, string> = {
  extrafield_projet: 'référence de projet portée par la conversation',
  contact_front: 'contact Front',
  domaine: 'domaine de messagerie',
  corps_message: 'corps du message',
};

export function methode(valeur: string | null | undefined): string {
  if (!valeur) return 'méthode non précisée';
  return METHODES[valeur] ?? lisible(valeur).toLowerCase();
}

const ETATS_RELANCE: Record<string, string> = {
  brouillon: 'Brouillon',
  programme: 'Programmée',
  envoye: 'Envoyée',
  annule: 'Annulée',
  repondu: 'Réponse reçue',
  echec: 'Échec',
};

export function etatRelance(valeur: string): string {
  return ETATS_RELANCE[valeur] ?? lisible(valeur);
}
